import type { TasksKanbanFilterParams, TasksKanbanResponse } from '../model/interfaces'
import type { TasksKanbanTeam } from '@shared/config'

import { useCommonStore } from '@shared/model'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { useTasksKanbanStore } from '../model/store'

interface KanbanTasksApiOptions {
  team: TasksKanbanTeam
  params?: TasksKanbanFilterParams
}

const buildApiParams = (team: TasksKanbanTeam, params?: TasksKanbanFilterParams): Record<string, string | string[]> => {
  const apiParams: Record<string, string | string[]> = { team }

  if (!params) return apiParams

  if (params.query) apiParams.query = params.query
  if (params.deadline_from) apiParams.deadline_from = params.deadline_from
  if (params.deadline_to) apiParams.deadline_to = params.deadline_to
  if (params.resolved_at_from) apiParams.resolved_at_from = params.resolved_at_from
  if (params.resolved_at_to) apiParams.resolved_at_to = params.resolved_at_to
  if (params.is_new) apiParams.is_new = params.is_new
  if (params.sort_by) apiParams.sort_by = params.sort_by
  if (params.sort_direction) apiParams.sort_direction = params.sort_direction
  if (params.related_to_id) apiParams.related_to_id = params.related_to_id
  if (params.related_to_type) apiParams.related_to_type = params.related_to_type
  if (params.assignee_id.length) apiParams.assignee_id = params.assignee_id
  if (params.task_type.length) apiParams.task_type = params.task_type
  if (params.priority.length) apiParams.priority = params.priority
  if (params.activation_type.length) apiParams.activation_type = params.activation_type
  if (params.alert_type.length) apiParams.alert_type = params.alert_type
  if (params.customer_id.length) apiParams.customer_id = params.customer_id

  return apiParams
}

export const useKanbanTasksApi = async (options: KanbanTasksApiOptions): Promise<TasksKanbanResponse | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const commonStore = useCommonStore()
  const tasksKanbanStore = useTasksKanbanStore()

  const url = useAdminRoutes('admin.kanban-tasks', buildApiParams(options.team, options.params))

  commonStore.setLoading(true)

  try {
    const { data } = await $axios.get(url!)
    const response: TasksKanbanResponse = data.data
    tasksKanbanStore.setTasks(response.tasks)
    tasksKanbanStore.setAgents(response.agents)
    commonStore.setLoading(false)

    return response
  } catch (err: unknown) {
    console.error(err)
    toast.error(getAxiosErrorMessage(err))
    commonStore.setLoading(false)

    return null
  }
}
